import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { Attachment } from '@/hooks/useChatSession';

interface AttachmentContextValue {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  staged: Attachment[];
  stageAttachment: (a: Attachment) => void;
  unstageAttachment: (chartId: string) => void;
  isStaged: (chartId: string) => boolean;
  clearStaged: () => void;
}

const Ctx = createContext<AttachmentContextValue | null>(null);
export const MAX_STAGED = 5;

export const AttachmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [staged, setStaged] = useState<Attachment[]>([]);

  const stageAttachment = useCallback((a: Attachment) => {
    setStaged(prev => {
      if (prev.some(x => x.chart_id === a.chart_id)) {
        // Toggle off if already staged with same id.
        return prev.filter(x => x.chart_id !== a.chart_id);
      }
      if (prev.length >= MAX_STAGED) return prev; // cap
      return [...prev, a];
    });
  }, []);

  const unstageAttachment = useCallback((chartId: string) => {
    setStaged(prev => prev.filter(x => x.chart_id !== chartId));
  }, []);

  const isStaged = useCallback(
    (chartId: string) => staged.some(x => x.chart_id === chartId),
    [staged]
  );

  const clearStaged = useCallback(() => setStaged([]), []);

  const value = useMemo(
    () => ({ drawerOpen, setDrawerOpen, staged, stageAttachment, unstageAttachment, isStaged, clearStaged }),
    [drawerOpen, staged, stageAttachment, unstageAttachment, isStaged, clearStaged]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAttachmentContext = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAttachmentContext must be used inside <AttachmentProvider>');
  return v;
};
